import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col } from "antd";
import { Icon } from "pepsico-ds";
import SingleSelectDropdown from "../../../components/Dropdown/SingleSelectDropdown";
import ActualLegendRISI from "../../../assets/ActualLegendRISI.svg";
import Vector from "../../../assets/Vector.png";
import { exportRowsToExcel } from "../../../utils/excelExport";
import { useToast } from "../../../components/Toast/ToastContext";
import {
  FetchWoodmacPageFilterAction,
  FetchWoodmacLineChartAction,
  FetchWoodmacTableAction
} from "../../../redux/action/SBACCIAction";
import {
  changeFromDateOptionsUtil,
  changeToDateOptionsUtil,
  getGeographyByProducts,
  getDropdownOptions,
  getGeographyByGrades,
  getPriceTypeByGeoAndGrade,
  getPricePointByGeoGradeAndPriceType,
  getPriceBasisByGeoGradePriceTypeAndPoint,
  getUnitByGeoGradePriceTypeAndPointAndBasis,
  getPriceForecastSeriesByGeoAndProduct,
  getPricePointByGeoProductAndPriceForecastSeries,
  getUnitByGeoProductAndPriceForecastSeriesPricePoint
} from "../../KPIOverview/KpiUtils";
import { getOverlappingOptionsV2 } from "../../../utils/utils";

const woodmacValueTypeList = [
  { displayText: "Actuals", id: "Actuals", label: "Actuals", value: "actual" },
  { displayText: "Forecast", id: "Forecast", label: "Forecast", value: "forecast" },
  { displayText: "Long Term Forecast", id: "LongTermForecast", label: "Long Term Forecast", value: "longTerm" }
];

// This context/provider owns ALL Woodmac Pricing-tab state, redux
// selectors, handler functions and effects so that Woodmac pricing logic is
// fully self-contained and can be shared between the two separate
// <WoodmacSource/> render sites in SBACCIScreen.js ("filters" and
// "content") without lifting state back up to the parent. The Reports tab
// for Woodmac continues to share its date-range/switcher plumbing with
// SBACCI in the parent, so that data is NOT part of this context and stays
// passed down to WoodmacSource as props.
const WoodmacSourceContext = createContext(null);

export const useWoodmacSource = () => useContext(WoodmacSourceContext);

export const WoodmacSourceProvider = ({ children, isUserDataFetched, selectedTab, isWoodmacSelected }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();

  const userData = useSelector(state => state.getUserInfoReducer);
  const woodmacFilterData = useSelector(state => state.getWoodmacPageFilterReducer);
  const woodmacLineChartData = useSelector(state => state.getWoodmacLineChartReducer);
  const woodmacTableData = useSelector(state => state.getWoodmacTableReducer);

  const isUserDataLoading = userData?.loading;
  const isFilterDataLoading = woodmacFilterData?.loading;
  const isLineDataLoading = woodmacLineChartData?.loading;
  const isTableDataLoading = woodmacTableData?.loading;
  const activePricingFilterData = woodmacFilterData;
  const activePricingLineChartData = woodmacLineChartData;
  const activePricingTableData = woodmacTableData;

  const [geographyList, setGeographyList] = useState([]);
  const [productList, setProductList] = useState([]);
  const [gradeList, setGradeList] = useState([]);
  const [priceTypeList, setPriceTypeList] = useState([]);
  const [priceTypeSelected, setPriceTypeSelected] = useState([]);
  const [pricePointList, setPricePointList] = useState([]);
  const [pricePointSelected, setPricePointSelected] = useState([]);
  const [pricePointWoodmacLongTermList, setpricePointWoodmacLongTermList] = useState([]);
  const [pricePointWoodmacLongTermSelected, setPricePointWoodmacLongTermSelected] = useState([]);
  const [priceBasisList, setPriceBasisList] = useState([]);
  const [priceBasisSelected, setPriceBasisSelected] = useState([]);
  const [unitList, setUnitList] = useState([]);
  const [priceForecastSeriesList, setPriceForecastSeriesList] = useState([]);
  const [priceForecastSeriesSelected, setPriceForecastSeriesSelected] = useState([]);
  const [unitWoodmacLongTermList, setUnitWoodmacLongTermList] = useState([]);
  const [unitsWoodmacLongTermSelected, setUnitsWoodmacLongTermSelected] = useState([]);
  const [unitsSelected, setUnitsSelected] = useState([]);
  const [geographySelected, setGeographySelected] = useState("");
  const [productSelected, setProductSelected] = useState("");
  const [gradeSelected, setGradeSelected] = useState("");
  const [woodmacValueTypeSelected, setwoodmacValueTypeSelected] = useState([]);
  const [woodmacPricingSwitcherIndex, setWoodmacPricingSwitcherIndex] = useState(0);

  const [cmaPricingFromDates, setCmaPricingFromDates] = useState([]);
  const [cmaPricingToDates, setCmaPricingToDates] = useState([]);
  const [cmaPricingFromDatesSelected, setCmaPricingFromDatesSelected] = useState([]);
  const [cmaPricingToDatesSelected, setCmaPricingToDatesSelected] = useState([]);
  const [entireCmaMonthList, setEntireCmaMonthList] = useState([]);

  const [resinChartData, setResinChartData] = useState([]);
  const [resinForecastStartVal, setResinForecastStartVal] = useState(null);
  const [resinSeriesLineChartIndex, setResinSeriesLineChartIndex] = useState([]);
  const [resinLineChartActualData, setResinLineChartActualData] = useState([]);
  const [resinFirstForecastYearMonth, setResinFirstForecastYearMonth] = useState(null);
  const [resinFirstForecastKeysToBeMergedActualLast, setResinFirstForecastKeysToBeMergedActualLast] = useState([]);
  const [currencySymbol, setCurrencySymbol] = useState("");

  const linechartRef = useRef();
  const cmaTableRef = useRef();

  const isWoodmacLongTermForecastSelected = woodmacValueTypeSelected?.[0]?.displayText === "Long Term Forecast";
  const isWoodmacActualsSelected = woodmacValueTypeSelected?.[0]?.displayText === "Actuals";
  const woodmacPricingFilterData = isWoodmacLongTermForecastSelected
    ? activePricingFilterData?.longtermForecasts?.product
    : isWoodmacActualsSelected
      ? activePricingFilterData?.actuals?.grade
      : activePricingFilterData?.forecasts?.grade;

  const cmaColumns = [
    { columnKey: "a", header: "" },
    {
      columnKey: "month",
      header: "MONTH",
      size: 90, minSize: 90, maxSize: 140,
      columnAlignment: "left",
      cellData: ctx => <span>{ctx.row.original.month}</span>
    },
    {
      columnKey: "price",
      size: 90, minSize: 90, maxSize: 140,
      header: "PRICE (" + (unitsSelected?.[0]?.displayText || "-") + ")",
      columnAlignment: "left",
      cellData: ctx => {
        const val = ctx.row.original.price;
        return <span>{val === null ? "--" : val}</span>;
      }
    },
    {
      columnKey: "momChange",
      header: "MOM CHANGE (" + (unitsSelected?.[0]?.displayText || "-") + ")",
      size: 90, minSize: 90, maxSize: 140,
      cellData: ctx => {
        const val = ctx.row.original.momChange;
        if (val === null) return <span>--</span>;
        return (

          <span 
            style={{ color: "green", display: "inline-flex", alignItems: "center", gap: 4 }}>
            {
              ctx.row.original.momChange < 0 ? (
                <span 
                  style={{ color: "#027C06", display: "inline-flex", 
                  alignItems: "center", gap: 4 }}>
                    <Icon alt="alt value" icon={"arrow_downward"} size="small" style={{ color: "#027C06" }} />
                    {val}
                </span>
            ) 
            : (
              <span style={{ color: "#C12C01", display: "inline-flex", 
                alignItems: "center", gap: 4 }}>
                  <Icon alt="alt value" icon={"arrow_upward"} size="small" style={{ color: "#C12C01" }} />
                {val}
              </span>
            )}
          </span>
        );
      },
      columnAlignment: "left"
    },
    {
      columnKey: "momChangePct",
      header: "MOM CHANGE %",
      size: 90, minSize: 90, maxSize: 140,
      cellData: ctx => {
        const val = ctx.row.original.momChangePct;
        if (val === null) return <span>--</span>;
        return (
          <span 
            style={
              { color: "green", display: "inline-flex", alignItems: "center", gap: 4 }
            }>
            {ctx.row.original.momChangePct < 0 ? 
            (
              <span 
                style={{ color: "#027C06", display: "inline-flex", alignItems: "center", gap: 4 }}>

                <Icon alt="alt value" 
                  icon={"arrow_downward"} 
                  size="small" 
                  style={{ color: "#027C06" }} />
                {val}
              </span>
            ) : (
              <span 
                style={{ color: "#C12C01", display: "inline-flex", alignItems: "center", gap: 4 }}>
                <Icon alt="alt value" 
                  icon={"arrow_upward"} size="small" 
                  style={{ color: "#C12C01" }} />
                {val}
              </span>
            )}
          </span>
        );
      },
      columnAlignment: "left"
    }
  ];

  /* istanbul ignore next */
  const monthLabelToObject = label => {

    if (!label) {
      return null; }

    const d = new Date(`${label} 01`);

    if (Number.isNaN(d.getTime())) { return null; }
    
    const month = d.getMonth() + 1;

    const year = d.getFullYear();

    const paddedMonth = String(month).padStart(2, "0");
    return {
      displayText: label,
      value: `${year}-${paddedMonth}-01`,
      id: `${year}-${month}`,
      label,
    };
  };

  /* istanbul ignore next */
  const generateMonthObjectsFromLabels = (minLabel, maxLabel) => {
    const result = [];
    if (!minLabel || !maxLabel) { return result; }

    const start = new Date(`${minLabel} 01`);
    const end = new Date(`${maxLabel} 01`);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) { return result; }
    const monthsDiff =
      (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    if (monthsDiff < 0) { return result; }
    const formatter = new Intl.DateTimeFormat("en-US", { month: "short", year: "numeric" });
    for (let i = 0; i <= monthsDiff; i++) {
      const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
      const month = d.getMonth() + 1;
      const displayText = formatter.format(d);
      const year = d.getFullYear();

      const paddedMonth = String(month).padStart(2, "0");
      result.push({ id: `${year}-${month}`, displayText, 
          label: displayText, value: `${year}-${paddedMonth}-01` 
      });
    }
    return result;
  };

  // function ensureFourDigitYear(label) {
  //   if (!label) return label;
  //   return label.replace(/(\w{3,})\s(\d{2})$/, (m, month, year) => `${month} 20${year}`);
  // }
  function ensureFourDigitYear(label) {
    if (!label) return label;
    const trimmedLabel = label.trim();
    const lastSpaceIndex = trimmedLabel.lastIndexOf(" ");
    if (lastSpaceIndex === -1) return label;
    const month = trimmedLabel.slice(0, lastSpaceIndex);
    const year = trimmedLabel.slice(lastSpaceIndex + 1);
    if (year.length === 2 && /^\d{2}$/.test(year)) {
      return `${month} 20${year}`;
    }
    return label;
  }

  const loadCMAData = () => {
    const woodmacPricingRequestObject = {
      value: JSON.parse(localStorage.getItem("woodmac-valueType"))?.[0]?.value,
      geography: JSON.parse(localStorage.getItem("woodmac-geo"))?.[0]?.displayText,
      product: JSON.parse(localStorage.getItem("woodmac-product"))?.[0]?.displayText || null,
      priceForecastSeries: JSON.parse(localStorage.getItem("woodmac-priceForecastSeries"))?.[0]?.displayText || null,
      grade: JSON.parse(localStorage.getItem("woodmac-grades"))?.[0]?.displayText || null,
      priceType: JSON.parse(localStorage.getItem("woodmac-priceTypes"))?.[0]?.displayText || null,
      pricePoint: isWoodmacLongTermForecastSelected
        ? JSON.parse(localStorage.getItem("woodmac-longterm-pricePoints"))?.[0]?.displayText || null
        : JSON.parse(localStorage.getItem("woodmac-pricePoints"))?.[0]?.displayText || null,
      priceBasis: JSON.parse(localStorage.getItem("woodmac-priceBasis"))?.[0]?.displayText || null,
      unit: isWoodmacLongTermForecastSelected
        ? JSON.parse(localStorage.getItem("woodmac-longterm-units"))?.[0]?.displayText || null
        : JSON.parse(localStorage.getItem("woodmac-units"))?.[0]?.displayText || null,
      fromDate: JSON.parse(localStorage.getItem("woodmac-fromDate"))?.[0]?.value || null,
      toDate: JSON.parse(localStorage.getItem("woodmac-toDate"))?.[0]?.value || null
    };

    dispatch(FetchWoodmacLineChartAction(woodmacPricingRequestObject));
    dispatch(FetchWoodmacTableAction(woodmacPricingRequestObject));
  };

  const initializePricingDateRanges = (woodmacValueTypeOverride = woodmacValueTypeSelected) => {
    const isLongTermValueTypeSelected = woodmacValueTypeOverride?.[0]?.displayText === "Long Term Forecast";
    const isActualsValueTypeSelected = woodmacValueTypeOverride?.[0]?.displayText === "Actuals";

    const dateInfo = isLongTermValueTypeSelected
      ? activePricingFilterData?.longtermForecasts?.date
      : isActualsValueTypeSelected
        ? activePricingFilterData?.actuals?.date
        : activePricingFilterData?.forecasts?.date;

    if (!dateInfo) return;

    const { minYearMonth, maxYearMonth, defaultMin, defaultMax } = dateInfo;
    const months = generateMonthObjectsFromLabels(
      ensureFourDigitYear(minYearMonth),
      ensureFourDigitYear(maxYearMonth)
    );
    if (!months.length) return;

    setEntireCmaMonthList(months);
    const defaultFrom = monthLabelToObject(ensureFourDigitYear(defaultMin)) || months[0];
    const defaultTo = monthLabelToObject(ensureFourDigitYear(defaultMax)) || months[months.length - 1];

    setCmaPricingFromDates(changeFromDateOptionsUtil(defaultTo, months));
    setCmaPricingToDates(changeToDateOptionsUtil(defaultFrom, months));
    setCmaPricingFromDatesSelected([defaultFrom]);
    setCmaPricingToDatesSelected([defaultTo]);
    localStorage.setItem("woodmac-fromDate", JSON.stringify([defaultFrom]));
    localStorage.setItem("woodmac-toDate", JSON.stringify([defaultTo]));
  };

  /* istanbul ignore next */
  const processResinChartData = (chartData = activePricingLineChartData) => {
    const { actual = [], forecast = [], denomination = "$", series = [] } = chartData || {};
    if (!actual.length && !forecast.length) return;

    setResinLineChartActualData(actual);

    const lastThreeActuals = actual.slice(-3);
    const firstThreeForecasts = forecast.slice(0, 3);
    const forecastMap = new Map(firstThreeForecasts.map(item => [item.yearMonth, item]));
    const keysArray = Object.keys(firstThreeForecasts[0] || {}).filter(
      key => key !== "yearMonth" && key !== "assessmentDate"
    );
    setResinFirstForecastKeysToBeMergedActualLast(keysArray);

    let firstForecastYM = null;
    let isFirstSet = false;
    const mergedActuals = actual.map(act => {
      const forecastMatch = forecastMap.get(act.yearMonth);
      if (forecastMatch) {
        if (!isFirstSet) {
          firstForecastYM = forecastMatch.yearMonth;
          isFirstSet = true;
        }
        return { ...act, ...forecastMatch, isForecastLabel: true };
      }
      return act;
    });
    setResinFirstForecastYearMonth(firstForecastYM);

    const mergedMonths = new Set(lastThreeActuals.map(a => a.yearMonth));
    const remainingForecasts = forecast.filter(f => !mergedMonths.has(f.yearMonth));
    const combined = [...mergedActuals, ...remainingForecasts];
    setResinChartData(combined);

    const forecastMonths = new Set(firstThreeForecasts.map(f => f.yearMonth));
    const firstMerged = lastThreeActuals.find(a => forecastMonths.has(a.yearMonth));
    setResinForecastStartVal(firstMerged?.yearMonth || forecast?.[0]?.yearMonth || null);

    setResinSeriesLineChartIndex(series);
    setCurrencySymbol(denomination);
  };

  const onValueTypeChange = val => {
    setWoodmacPricingSwitcherIndex(val);

    const valueTypeMap = [
      { displayText: "Actuals", id: "Actuals", value: "actual" },
      { displayText: "Forecast", id: "Forecast", value: "forecast" },
      { displayText: "Long Term Forecast", id: "LongTermForecast", value: "longTerm" }
    ];

    const selected = valueTypeMap[val];
    if (selected) {
      val = [{ ...selected, childContents: [] }];
    }

    if (val && val.length > 0) {
      setwoodmacValueTypeSelected(val);
      localStorage.setItem("woodmac-valueType", JSON.stringify(val));

      dispatch(FetchWoodmacPageFilterAction());
    }
  };

  const onGeographyChange = val => {
    if (val && val.length > 0) {
      const selectedGeo = val;
      setGeographySelected(val);
      const data = woodmacPricingFilterData;

      localStorage.setItem("woodmac-geo", JSON.stringify(val));

      if (isWoodmacLongTermForecastSelected) {
        const selectedProduct = productSelected;

        const allPriceForecastSeries = getPriceForecastSeriesByGeoAndProduct(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText);
        setPriceForecastSeriesList(allPriceForecastSeries);
        const selectedPriceForecastSeries = allPriceForecastSeries?.[0] ? [allPriceForecastSeries[0]] : [];
        setPriceForecastSeriesSelected(selectedPriceForecastSeries);
        localStorage.setItem("woodmac-priceForecastSeries", JSON.stringify(selectedPriceForecastSeries));

        const pricePoints = getPricePointByGeoProductAndPriceForecastSeries(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText, selectedPriceForecastSeries?.[0]?.displayText);
        setpricePointWoodmacLongTermList(pricePoints);
        setPricePointWoodmacLongTermSelected([pricePoints?.[0]]);
        localStorage.setItem("woodmac-longterm-pricePoints", JSON.stringify([pricePoints?.[0]]));

        const units = getUnitByGeoProductAndPriceForecastSeriesPricePoint(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText, selectedPriceForecastSeries?.[0]?.displayText, pricePoints?.[0]?.displayText);
        setUnitWoodmacLongTermList(units);
        setUnitsWoodmacLongTermSelected([units?.[0]]);
        setUnitsSelected([units?.[0]]);
        localStorage.setItem("woodmac-longterm-units", JSON.stringify([units?.[0]]));
      } else {
        const selectedGrade = gradeSelected;

        const priceTypes = getPriceTypeByGeoAndGrade(data, selectedGeo?.[0]?.displayText, 
            selectedGrade?.[0]?.displayText);
            
        setPriceTypeSelected([priceTypes?.[0]]);
        setPriceTypeList(priceTypes);
        localStorage.setItem("woodmac-priceTypes", JSON.stringify([priceTypes?.[0]]));

        const pricePoints = getPricePointByGeoGradeAndPriceType(data, selectedGeo?.[0]?.displayText, selectedGrade?.[0]?.displayText, priceTypes?.[0]?.displayText);
        localStorage.setItem("woodmac-pricePoints", JSON.stringify([pricePoints?.[0]]));
        setPricePointSelected([pricePoints?.[0]]);
        setPricePointList(pricePoints);

        const priceBasis = 
          getPriceBasisByGeoGradePriceTypeAndPoint(data, selectedGeo?.[0]?.displayText, 
            selectedGrade?.[0]?.displayText, priceTypes?.[0]?.displayText, pricePoints?.[0]?.displayText);
        setPriceBasisList(priceBasis);
        localStorage.setItem("woodmac-priceBasis", JSON.stringify([priceBasis?.[0]]));
        setPriceBasisSelected([priceBasis?.[0]]);

        const units = 
          getUnitByGeoGradePriceTypeAndPointAndBasis(data, selectedGeo?.[0]?.displayText, selectedGrade?.[0]?.displayText, priceTypes?.[0]?.displayText, pricePoints?.[0]?.displayText, priceBasis?.[0]?.displayText);
        
        setUnitsSelected([units?.[0]]);
        setUnitList(units);
        localStorage.setItem("woodmac-units", JSON.stringify([units?.[0]]));
      }

      loadCMAData();
    }
  };

  const onProductChange = val => {
    if (val && val.length > 0) {
      const selectedProduct = val;
      setProductSelected(selectedProduct);
      const data = woodmacPricingFilterData;

      localStorage.setItem("woodmac-product", JSON.stringify(selectedProduct));

      if (isWoodmacLongTermForecastSelected) {
        const geographies = getGeographyByProducts(data, selectedProduct?.[0]?.displayText);
        setGeographyList(geographies);
        const preferredGeography = getOverlappingOptionsV2(geographies, [geographySelected?.[0]?.displayText].filter(Boolean));
        const selectedGeo = preferredGeography?.length ? preferredGeography : geographies.slice(0, 1);
        setGeographySelected(selectedGeo);
        localStorage.setItem("woodmac-geo", JSON.stringify(selectedGeo));

        const allPriceForecastSeries = getPriceForecastSeriesByGeoAndProduct(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText);
        setPriceForecastSeriesList(allPriceForecastSeries);
        const selectedPriceForecastSeries = allPriceForecastSeries?.[0] ? [allPriceForecastSeries[0]] : [];
        setPriceForecastSeriesSelected(selectedPriceForecastSeries);
        localStorage.setItem("woodmac-priceForecastSeries", JSON.stringify(selectedPriceForecastSeries));

        const pricePoints = getPricePointByGeoProductAndPriceForecastSeries(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText, selectedPriceForecastSeries?.[0]?.displayText);
        setpricePointWoodmacLongTermList(pricePoints);
        setPricePointWoodmacLongTermSelected([pricePoints?.[0]]);
        localStorage.setItem("woodmac-longterm-pricePoints", JSON.stringify([pricePoints?.[0]]));

        const units = getUnitByGeoProductAndPriceForecastSeriesPricePoint(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText, selectedPriceForecastSeries?.[0]?.displayText, pricePoints?.[0]?.displayText);
        setUnitWoodmacLongTermList(units);
        setUnitsWoodmacLongTermSelected([units?.[0]]);
        setUnitsSelected([units?.[0]]);
        localStorage.setItem("woodmac-longterm-units", JSON.stringify([units?.[0]]));
      }

      loadCMAData();
    }
  };

  const onGradeChange = val => {
    if (val && val.length > 0) {
      const data = woodmacPricingFilterData;

      setGradeSelected(val);
      localStorage.setItem("woodmac-grades", JSON.stringify(val));

      const geographies = getGeographyByGrades(data, val?.[0]?.displayText);
      setGeographyList(geographies);
      const preferredGeography = getOverlappingOptionsV2(geographies, [geographySelected?.[0]?.displayText].filter(Boolean));
      const selectedGeo = preferredGeography?.length ? preferredGeography : geographies.slice(0, 1);
      setGeographySelected(selectedGeo);
      localStorage.setItem("woodmac-geo", JSON.stringify(selectedGeo));

      const priceTypes = getPriceTypeByGeoAndGrade(data, selectedGeo?.[0]?.displayText, val?.[0]?.displayText);
      setPriceTypeList(priceTypes);
      setPriceTypeSelected([priceTypes?.[0]]);
      localStorage.setItem("woodmac-priceTypes", JSON.stringify([priceTypes?.[0]]));

      const pricePoints = getPricePointByGeoGradeAndPriceType(data, selectedGeo?.[0]?.displayText, val?.[0]?.displayText, priceTypes?.[0]?.displayText);
      setPricePointList(pricePoints);
      setPricePointSelected([pricePoints?.[0]]);
      localStorage.setItem("woodmac-pricePoints", JSON.stringify([pricePoints?.[0]]));

      const priceBasis = getPriceBasisByGeoGradePriceTypeAndPoint(data, selectedGeo?.[0]?.displayText, val?.[0]?.displayText, priceTypes?.[0]?.displayText, pricePoints?.[0]?.displayText);
      setPriceBasisList(priceBasis);
      setPriceBasisSelected([priceBasis?.[0]]);
      localStorage.setItem("woodmac-priceBasis", JSON.stringify([priceBasis?.[0]]));

      const units = getUnitByGeoGradePriceTypeAndPointAndBasis(data, selectedGeo?.[0]?.displayText, val?.[0]?.displayText, priceTypes?.[0]?.displayText, pricePoints?.[0]?.displayText, priceBasis?.[0]?.displayText);
      setUnitList(units);
      setUnitsSelected([units?.[0]]);
      localStorage.setItem("woodmac-units", JSON.stringify([units?.[0]]));

      loadCMAData();
    }
  };

  const onPriceTrypeChange = val => {
    if (val && val.length > 0) {
      if (!isWoodmacLongTermForecastSelected) {
        const data = woodmacPricingFilterData;

        setPriceTypeSelected(val);
        localStorage.setItem("woodmac-priceTypes", JSON.stringify(val));

        const pricePoints = getPricePointByGeoGradeAndPriceType(data, geographySelected?.[0]?.displayText, gradeSelected?.[0]?.displayText, val?.[0]?.displayText);
        setPricePointList(pricePoints);
        setPricePointSelected([pricePoints?.[0]]);
        localStorage.setItem("woodmac-pricePoints", JSON.stringify([pricePoints?.[0]]));

        const priceBasis = getPriceBasisByGeoGradePriceTypeAndPoint(data, geographySelected?.[0]?.displayText, gradeSelected?.[0]?.displayText, val?.[0]?.displayText, pricePoints?.[0]?.displayText);
        setPriceBasisList(priceBasis);
        setPriceBasisSelected([priceBasis?.[0]]);
        localStorage.setItem("woodmac-priceBasis", JSON.stringify([priceBasis?.[0]]));

        const units = getUnitByGeoGradePriceTypeAndPointAndBasis(data, geographySelected?.[0]?.displayText, gradeSelected?.[0]?.displayText, val?.[0]?.displayText, pricePoints?.[0]?.displayText, priceBasis?.[0]?.displayText);
        setUnitList(units);
        setUnitsSelected([units?.[0]]);
        localStorage.setItem("woodmac-units", JSON.stringify([units?.[0]]));
      }

      loadCMAData();
    }
  };

  const onWoodmacPriceForecastSeriesChange = val => {
    if (val && val.length > 0) {
      if (isWoodmacLongTermForecastSelected) {
        const data = activePricingFilterData?.longtermForecasts?.product;

        setPriceForecastSeriesSelected(val);
        localStorage.setItem("woodmac-priceForecastSeries", JSON.stringify(val));

        const pricePoints = getPricePointByGeoProductAndPriceForecastSeries(data, geographySelected?.[0]?.displayText, productSelected?.[0]?.displayText, val?.[0]?.displayText);
        setpricePointWoodmacLongTermList(pricePoints);
        setPricePointWoodmacLongTermSelected([pricePoints?.[0]]);
        localStorage.setItem("woodmac-longterm-pricePoints", JSON.stringify([pricePoints?.[0]]));

        const units = getUnitByGeoProductAndPriceForecastSeriesPricePoint(data, geographySelected?.[0]?.displayText, productSelected?.[0]?.displayText, val?.[0]?.displayText, pricePoints?.[0]?.displayText);
        setUnitWoodmacLongTermList(units);
        setUnitsWoodmacLongTermSelected([units?.[0]]);
        setUnitsSelected([units?.[0]]);
        localStorage.setItem("woodmac-longterm-units", JSON.stringify([units?.[0]]));
      }

      loadCMAData();
    }
  };

  const onUnitsChange = val => {
    if (val && val.length > 0) {
      setUnitsSelected(val);
      if (isWoodmacLongTermForecastSelected) {
        setUnitsWoodmacLongTermSelected(val);
        localStorage.setItem("woodmac-longterm-units", JSON.stringify(val));
      } else {
        localStorage.setItem("woodmac-units", JSON.stringify(val));
      }

      loadCMAData();
    }
  };

  const changeCmaFromDateOptions = toDateMonthSelected => {
    const filteredMonths = changeFromDateOptionsUtil(toDateMonthSelected, entireCmaMonthList);
    setCmaPricingFromDates(filteredMonths);
  };

  const changeCmaToDateOptions = fromDateMonthSelected => {
    const filteredMonths = changeToDateOptionsUtil(fromDateMonthSelected, entireCmaMonthList);
    setCmaPricingToDates(filteredMonths);
  };

  const onPETResinFromDateChange = val => {
    if (val && val.length > 0) {
      setCmaPricingFromDatesSelected(val);
      changeCmaToDateOptions(val[0]);
      localStorage.setItem("woodmac-fromDate", JSON.stringify(val));
      loadCMAData();
    }
  };

  const onPETResinToDateChange = val => {
    if (val && val.length > 0) {
      setCmaPricingToDatesSelected(val);
      changeCmaFromDateOptions(val[0]);
      localStorage.setItem("woodmac-toDate", JSON.stringify(val));
      loadCMAData();
    }
  };

  const resetButtonHandler = () => {
    dispatch(FetchWoodmacPageFilterAction());
  };

  /* istanbul ignore next */
  const downloadExcel = async () => {
    const columnsToInclude = ["MONTH", "PRICE (" + (unitsSelected?.[0]?.displayText || "-") + ")", "MOM CHANGE (" + (unitsSelected?.[0]?.displayText || "-") + ")", "MOM CHANGE %"];

    const columnMapping = {
      month: "MONTH",
      price: "PRICE (" + (unitsSelected?.[0]?.displayText || "-") + ")",
      momChange: "MOM CHANGE (" + (unitsSelected?.[0]?.displayText || "-") + ")",
      momChangePct: "MOM CHANGE %"
    };

    const transformedData = 
    activePricingTableData?.ProductPriceDetails
    ?.map(item => {
        const newItem = {};
        columnsToInclude.forEach(col => {

          const originalKey = Object.keys(columnMapping).find(key => columnMapping[key] === col);
          newItem[col] = item[originalKey];
        });

        return newItem;
    });

    await exportRowsToExcel({
      rows: transformedData,
      headers: columnsToInclude,
      fileName: "Product Price Details.xlsx",
    });
  };

  /* istanbul ignore next */
  const checkPDFDownloaded = (isPdfDownloaded, isModal, fileURL, isExcel, isTable, isJpeg) => {
    if (isPdfDownloaded) {
      if (isModal) {
        showToast("success", !isJpeg ? "PDF of full page successfully downloaded." : "JPEG of full page successfully downloaded.", {
          icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
        });
      } else if (isTable) {
        showToast("success", !isJpeg ? "PDF of a table successfully downloaded." : "JPEG of a table successfully downloaded.", {
          icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
        });
      } else {
        showToast("success", !isJpeg ? "PDF of a chart successfully downloaded." : "JPEG of a chart successfully downloaded.", {
          icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
        });
      }
    } else if (isExcel) {
      downloadExcel();
    } else {
      showToast("Error", "Failed to download. Please try again.", {
        icon: <img src={Vector} alt="image" style={{ width: "20px", height: "20px" }} />
      });
    }
  };

  const filterLabelStyle = {
    fontFamily: "Inter",
    fontSize: "0.75rem",
    fontWeight: "500",
    marginBottom: "3px"
  };

  const renderPricingFilterDropdown = (label, childList, selectedValue, setSelectedValue, colProps = {}) => (
    <Col {...colProps}>
      <div style={filterLabelStyle}>{label}</div>
      <SingleSelectDropdown
        className="buy-con-dropdown-modal cma-dropdown"
        childList={childList}
        selectedValue={selectedValue}
        setSelectedValue={setSelectedValue}
      />
    </Col>
  );

  /* istanbul ignore next */
  const renderForecastLegend = () => (
    <div>
      <img src={ActualLegendRISI} alt="img" style={{ marginRight: "1rem" }} />

        <span style={{ marginRight: "0.25rem", position: "relative", top: "3px" }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <line x1="0" y1="6" x2="12" y2="6" 
              stroke={"#616161"} strokeWidth="2" strokeLinecap="round" strokeDasharray="5 0" />
          </svg>
        </span>

        <span style={{ marginRight: "0.25rem", position: "relative", top: "3px" }}>
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
            <line x1="0" y1="6" x2="18" y2="6" stroke={"#616161"} 
              strokeWidth="2" strokeLinecap="round" strokeDasharray="5 0" />
          </svg>
        </span>
      <span style={{ color: "#616161", 
        position: "relative", top: "2px" }}>Forecast</span>
    </div>
  );

  // Fetch Woodmac filter data whenever Woodmac becomes the active source
  // (covers both switching to Woodmac from another source and the initial
  // load if Woodmac is the default) once user info has been fetched.
  // isWoodmacSelected is included so this refires on every switch back to
  // Woodmac even though this provider itself stays mounted for the lifetime
  // of SBACCIScreen.
  useEffect(() => {
    if (isUserDataFetched && isWoodmacSelected) {
      const defaultValueType = [woodmacValueTypeList[0]];
      setwoodmacValueTypeSelected(defaultValueType);
      localStorage.setItem("woodmac-valueType", JSON.stringify(defaultValueType));
      setWoodmacPricingSwitcherIndex(0);
      dispatch(FetchWoodmacPageFilterAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUserDataFetched, isWoodmacSelected]);

  useEffect(() => {
    if (isUserDataFetched && woodmacFilterData && woodmacFilterData.type === "SUCCESS_WOODMAC_PAGE_FILTER_ACTION") {
      const selectedWoodmacValueType =
        woodmacValueTypeSelected?.length > 0 ? woodmacValueTypeSelected : [woodmacValueTypeList[0]];

      if (!woodmacValueTypeSelected?.length) {
        setwoodmacValueTypeSelected(selectedWoodmacValueType);
        localStorage.setItem("woodmac-valueType", JSON.stringify(selectedWoodmacValueType));
      }

      const isLongTermValueTypeSelected = selectedWoodmacValueType?.[0]?.displayText === "Long Term Forecast";
      const isActualsValueTypeSelected = selectedWoodmacValueType?.[0]?.displayText === "Actuals";

      const data = isLongTermValueTypeSelected
        ? woodmacFilterData?.longtermForecasts?.product
        : isActualsValueTypeSelected
          ? woodmacFilterData?.actuals?.grade
          : woodmacFilterData?.forecasts?.grade;
      const selectedDefaultSelection = isLongTermValueTypeSelected
        ? woodmacFilterData?.longtermForecasts?.defaultSelection
        : isActualsValueTypeSelected
          ? woodmacFilterData?.actuals?.defaultSelection
          : woodmacFilterData?.forecasts?.defaultSelection;
      const allProducts = isLongTermValueTypeSelected
        ? getDropdownOptions([...new Set((data || []).map(product => product.name))])
        : [];
      const preferredProduct = getOverlappingOptionsV2(allProducts, [selectedDefaultSelection?.defaultProduct].filter(Boolean));
      const selectedProduct = preferredProduct?.length ? preferredProduct : allProducts.slice(0, 1);
      if (isLongTermValueTypeSelected) {
        setProductList(allProducts);
        setProductSelected(selectedProduct);
        localStorage.setItem("woodmac-product", JSON.stringify(selectedProduct));
      }

      const allGrades = !isLongTermValueTypeSelected
        ? getDropdownOptions([...new Set((data || []).map(grade => grade.name))])
        : [];
      const preferredGrade = getOverlappingOptionsV2(allGrades, [selectedDefaultSelection?.defaultGrade].filter(Boolean));
      const selectedGrade = preferredGrade?.length ? preferredGrade : allGrades.slice(0, 1);
      if (!isLongTermValueTypeSelected) {
        setGradeList(allGrades);
        setGradeSelected(selectedGrade);
        localStorage.setItem("woodmac-grades", JSON.stringify(selectedGrade));
      }

      const geoList = isLongTermValueTypeSelected
        ? getGeographyByProducts(data, selectedProduct?.[0]?.displayText)
        : getGeographyByGrades(data, selectedGrade?.[0]?.displayText);
      setGeographyList(geoList);
      const preferredGeography = getOverlappingOptionsV2(geoList, [selectedDefaultSelection?.defaultGeography].filter(Boolean));
      const selectedGeo = preferredGeography?.length ? preferredGeography : geoList.slice(0, 1);
      setGeographySelected(selectedGeo);
      localStorage.setItem("woodmac-geo", JSON.stringify(selectedGeo));

      if (isLongTermValueTypeSelected) {
        const allPriceForecastSeries = getPriceForecastSeriesByGeoAndProduct(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText);
        setPriceForecastSeriesList(allPriceForecastSeries);
        const selectedPriceForecastSeries = getOverlappingOptionsV2(allPriceForecastSeries, [woodmacFilterData?.longtermForecasts?.defaultSelection?.defaultPriceForecastSeries].filter(Boolean));
        setPriceForecastSeriesSelected(selectedPriceForecastSeries);
        localStorage.setItem("woodmac-priceForecastSeries", JSON.stringify(selectedPriceForecastSeries));

        const pricePoints = getPricePointByGeoProductAndPriceForecastSeries(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText, selectedPriceForecastSeries?.[0]?.displayText);
        setpricePointWoodmacLongTermList(pricePoints);
        setPricePointWoodmacLongTermSelected([pricePoints?.[0]]);
        localStorage.setItem("woodmac-longterm-pricePoints", JSON.stringify([pricePoints?.[0]]));

        const units = getUnitByGeoProductAndPriceForecastSeriesPricePoint(data, selectedGeo?.[0]?.displayText, selectedProduct?.[0]?.displayText, selectedPriceForecastSeries?.[0]?.displayText, pricePoints?.[0]?.displayText);
        setUnitWoodmacLongTermList(units);
        setUnitsWoodmacLongTermSelected([units?.[0]]);
        setUnitsSelected([units?.[0]]);
        localStorage.setItem("woodmac-longterm-units", JSON.stringify([units?.[0]]));
      } else {
        const priceTypes = getPriceTypeByGeoAndGrade(data, selectedGeo?.[0]?.displayText, selectedGrade?.[0]?.displayText);
        setPriceTypeList(priceTypes);
        setPriceTypeSelected([priceTypes?.[0]]);
        localStorage.setItem("woodmac-priceTypes", JSON.stringify([priceTypes?.[0]]));

        const pricePoints = getPricePointByGeoGradeAndPriceType(data, selectedGeo?.[0]?.displayText, selectedGrade?.[0]?.displayText, priceTypes?.[0]?.displayText);
        setPricePointList(pricePoints);
        setPricePointSelected([pricePoints?.[0]]);
        localStorage.setItem("woodmac-pricePoints", JSON.stringify([pricePoints?.[0]]));

        const priceBasis = getPriceBasisByGeoGradePriceTypeAndPoint(data, selectedGeo?.[0]?.displayText, selectedGrade?.[0]?.displayText, priceTypes?.[0]?.displayText, pricePoints?.[0]?.displayText);
        setPriceBasisList(priceBasis);
        setPriceBasisSelected([priceBasis?.[0]]);
        localStorage.setItem("woodmac-priceBasis", JSON.stringify([priceBasis?.[0]]));

        const units = getUnitByGeoGradePriceTypeAndPointAndBasis(data, selectedGeo?.[0]?.displayText, selectedGrade?.[0]?.displayText, priceTypes?.[0]?.displayText, pricePoints?.[0]?.displayText, priceBasis?.[0]?.displayText);
        setUnitList(units);
        setUnitsSelected([units?.[0]]);
        localStorage.setItem("woodmac-units", JSON.stringify([units?.[0]]));
      }

      initializePricingDateRanges(selectedWoodmacValueType);
      loadCMAData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [woodmacFilterData, isUserDataFetched]);

  useEffect(() => {
    processResinChartData(woodmacLineChartData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [woodmacLineChartData]);

  const value = {
    selectedTab,
    woodmacPricingSwitcherIndex, 
    onValueTypeChange,
    isWoodmacLongTermForecastSelected,
    priceForecastSeriesList, priceForecastSeriesSelected, onWoodmacPriceForecastSeriesChange,
    productList, productSelected, onProductChange,
    geographyList, geographySelected, onGeographyChange,
    gradeList, gradeSelected, onGradeChange,
    priceTypeList, priceTypeSelected, onPriceTrypeChange,
    unitWoodmacLongTermList, unitsWoodmacLongTermSelected,
    cmaPricingToDates, cmaPricingToDatesSelected, onPETResinToDateChange,
    cmaPricingFromDates, cmaPricingFromDatesSelected, onPETResinFromDateChange,
    unitList, unitsSelected, onUnitsChange,
    renderPricingFilterDropdown,
    resetButtonHandler,pricePointSelected,
    priceBasisSelected,
    activePricingLineChartData,
    resinChartData,
    resinLineChartActualData,
    isUserDataLoading, isFilterDataLoading, isLineDataLoading,
    linechartRef,
    checkPDFDownloaded,resinForecastStartVal,resinSeriesLineChartIndex,resinFirstForecastYearMonth,
    resinFirstForecastKeysToBeMergedActualLast,
    currencySymbol,
    renderForecastLegend,isTableDataLoading,
    cmaTableRef,activePricingTableData,cmaColumns
  };

  return <WoodmacSourceContext.Provider value={value}>{children}</WoodmacSourceContext.Provider>;
};
